import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
  Textsms as TextsmsIcon,
} from "@mui/icons-material";
import { supabase } from "../../lib/supabase";
import {
  addClientShortlistItem,
  buildPublicShortlistUrl,
  getClientShortlistDetail,
  getOrCreateClientShortlist,
  listClientShortlists,
  removeClientShortlistItem,
  updateClientShortlistItem,
  type ClientShortlistDetail,
  type ClientShortlistProperty,
} from "../../services/clientShortlistService";
import {
  confirmShortlistSmsSent,
  handoffShortlistToNativeSms,
  type ShortlistSmsHandoffReceipt,
} from "../../services/shortlistSmsHandoff";
import type { Client, SavedSearch, SentListing } from "./types";
import { formatRelativeTime } from "./helpers";

interface ClientShortlistPanelProps {
  client: Client;
  sentListings?: SentListing[];
  savedSearches?: SavedSearch[];
}

const DEAD_LINK_STATUSES = new Set(["out_of_contact", "closed"]);

// Module-level so the optional-prop defaults keep a stable identity. An inline
// `= []` default builds a new array every render, which would retrigger the
// floor-plan effect below on every pass.
const EMPTY_SENT_LISTINGS: SentListing[] = [];
const EMPTY_SAVED_SEARCHES: SavedSearch[] = [];

interface FloorPlanLookupRow {
  id: string;
  property_id: string;
  model_name?: string | null;
}

function formatRent(property: ClientShortlistProperty): string {
  if (property.min_rent && property.max_rent && property.max_rent !== property.min_rent) {
    return `$${property.min_rent.toLocaleString()}-${property.max_rent.toLocaleString()}`;
  }
  if (property.min_rent) return `From $${property.min_rent.toLocaleString()}`;
  if (property.max_rent) return `Up to $${property.max_rent.toLocaleString()}`;
  return "Rent not listed";
}

function buildSentListMetadata(listing: SentListing, sentProperty: any) {
  const units = Array.isArray(sentProperty?.units) ? sentProperty.units : [];
  const floorPlanIds = Array.from(
    new Set(
      units
        .map((unit: any) => unit?.floor_plan_id)
        .filter((id: unknown): id is string => typeof id === "string" && id.length > 0)
    )
  );
  const floorPlanSlugs: Record<string, string> = {};
  units.forEach((unit: any) => {
    if (unit?.floor_plan_id && unit?.floorplan_slug) {
      floorPlanSlugs[unit.floor_plan_id] = unit.floorplan_slug;
    }
  });

  return {
    selection_mode: floorPlanIds.length > 0 ? "floorplan" : "property",
    source: "sent_list",
    sent_at: listing.sent_at,
    tracking_id: listing.tracking_id || null,
    floor_plan_ids: floorPlanIds,
    floor_plan_slugs: floorPlanSlugs,
    units: units.map((unit: any) => ({
      id: unit?.id || null,
      model_name: unit?.model_name || null,
      bedrooms: unit?.bedrooms ?? null,
      bathrooms: unit?.bathrooms ?? null,
      square_feet: unit?.square_feet ?? null,
      current_price: unit?.current_price ?? null,
      status: unit?.status || null,
      available_date: unit?.available_date || null,
      floorplan_url: unit?.floorplan_url || null,
      floor_plan_id: unit?.floor_plan_id || null,
      floorplan_slug: unit?.floorplan_slug || null,
    })),
  };
}

function normalizeModelName(value: unknown): string {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function getFloorPlanCount(metadata?: Record<string, unknown>): number {
  const ids = metadata?.floor_plan_ids;
  return Array.isArray(ids)
    ? ids.filter((id) => typeof id === "string" && id.length > 0).length
    : 0;
}

function enrichLegacySentListMetadata(
  metadata: Record<string, unknown> | undefined,
  floorPlans: FloorPlanLookupRow[]
): Record<string, unknown> | undefined {
  if (!metadata) return metadata;
  const existingIds = Array.isArray(metadata.floor_plan_ids)
    ? metadata.floor_plan_ids.filter(
        (id): id is string => typeof id === "string" && id.length > 0
      )
    : [];
  if (existingIds.length > 0) return metadata;

  const units = Array.isArray(metadata.units) ? metadata.units : [];
  if (units.length === 0 || floorPlans.length === 0) return metadata;

  const resolvedIds: string[] = [];
  const updatedUnits = units.map((unit: any) => {
    if (unit?.floor_plan_id) return unit;
    const normalizedModel = normalizeModelName(unit?.model_name);
    if (!normalizedModel) return unit;

    const exactMatches = floorPlans.filter(
      (floorPlan) => normalizeModelName(floorPlan.model_name) === normalizedModel
    );
    if (exactMatches.length !== 1) return unit;

    const floorPlanId = exactMatches[0].id;
    if (!resolvedIds.includes(floorPlanId)) {
      resolvedIds.push(floorPlanId);
    }
    return {
      ...unit,
      floor_plan_id: floorPlanId,
    };
  });

  if (resolvedIds.length === 0) return metadata;

  return {
    ...metadata,
    selection_mode: "floorplan",
    floor_plan_ids: resolvedIds,
    units: updatedUnits,
    legacy_floor_plan_resolution: {
      strategy: "property_id_model_name_exact",
      resolved_at: new Date().toISOString(),
    },
  };
}

function criteriaObject(search: SavedSearch): Record<string, unknown> {
  return search.search_criteria && typeof search.search_criteria === "object"
    ? (search.search_criteria as Record<string, unknown>)
    : {};
}

function getCriteriaStrings(criteria: Record<string, unknown>, keys: string[]): string[] {
  for (const key of keys) {
    const value = criteria[key];
    if (Array.isArray(value)) {
      return value
        .map((item) => (typeof item === "string" ? item.trim() : ""))
        .filter(Boolean);
    }
    if (typeof value === "string" && value.trim()) {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }
  return [];
}

function getCriteriaNumber(criteria: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const value = criteria[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim()) {
      const parsed = Number(value.replace(/[$,]/g, ""));
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function getSelectedFloorPlanKeys(search: SavedSearch): string[] {
  return getCriteriaStrings(criteriaObject(search), ["selected_floor_plan_keys"]);
}

function getSelectedUnitIds(search: SavedSearch): string[] {
  return getCriteriaStrings(criteriaObject(search), ["selected_unit_ids"]);
}

const SAVED_SEARCH_FLOOR_PLAN_KEY_PROPERTY_ID_PATTERN =
  /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})-/i;

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)));
}

function extractPropertyIdFromFloorPlanKey(key: string): string | null {
  const match = key.match(SAVED_SEARCH_FLOOR_PLAN_KEY_PROPERTY_ID_PATTERN);
  return match?.[1] || null;
}

function getSelectedPropertyIds(search: SavedSearch): string[] {
  return getCriteriaStrings(criteriaObject(search), ["selected_property_ids", "selectedPropertyIds"]);
}

function getExplicitSavedSearchPropertyIds(search: SavedSearch): string[] {
  const checkedFloorPlanPropertyIds = getSelectedFloorPlanKeys(search)
    .map(extractPropertyIdFromFloorPlanKey)
    .filter((id): id is string => Boolean(id));

  if (checkedFloorPlanPropertyIds.length > 0) {
    return uniqueStrings(checkedFloorPlanPropertyIds);
  }

  if (getSelectedUnitIds(search).length > 0) {
    return [];
  }

  return uniqueStrings(getSelectedPropertyIds(search));
}

function parseSavedSearchFloorPlanKey(
  propertyId: string,
  key: string
): Record<string, unknown> | null {
  const prefix = `${propertyId}-`;
  if (!key.startsWith(prefix)) return null;

  const rest = key.slice(prefix.length);
  const pieces = rest.split("-");
  if (pieces.length < 4) return null;

  const squareFeetRaw = pieces.pop();
  const bathroomsRaw = pieces.pop();
  const bedroomsRaw = pieces.pop();
  const modelName = pieces.join("-").trim();
  if (!modelName) return null;

  const bedrooms = bedroomsRaw ? Number(bedroomsRaw) : null;
  const bathrooms = bathroomsRaw ? Number(bathroomsRaw) : null;
  const squareFeet = squareFeetRaw ? Number(squareFeetRaw) : null;

  return {
    model_name: modelName,
    bedrooms: Number.isFinite(bedrooms) ? bedrooms : null,
    bathrooms: Number.isFinite(bathrooms) ? bathrooms : null,
    square_feet: Number.isFinite(squareFeet) ? squareFeet : null,
    saved_search_floor_plan_key: key,
  };
}

function savedSearchHasSelectedFloorPlans(search: SavedSearch): boolean {
  return getSelectedFloorPlanKeys(search).length > 0;
}

function savedSearchHasSelectedUnits(search: SavedSearch): boolean {
  return getSelectedUnitIds(search).length > 0;
}

function savedSearchMatchesProperty(
  search: SavedSearch,
  property: ClientShortlistProperty
): boolean {
  return getExplicitSavedSearchPropertyIds(search).includes(property.id);
}

function buildSavedSearchMetadata(
  search: SavedSearch,
  propertyId: string
): Record<string, unknown> {
  const criteria = criteriaObject(search);
  const selectedFloorPlanKeys = getSelectedFloorPlanKeys(search).filter((key) =>
    key.startsWith(`${propertyId}-`)
  );
  const selectedUnitIds = getSelectedUnitIds(search);
  const selectedPropertyIds = getExplicitSavedSearchPropertyIds(search);
  const units = selectedFloorPlanKeys
    .map((key) => parseSavedSearchFloorPlanKey(propertyId, key))
    .filter((unit): unit is Record<string, unknown> => Boolean(unit));

  return {
    selection_mode:
      units.length > 0 ? "floorplan" : selectedUnitIds.length > 0 ? "unit" : "property",
    source: "saved_search",
    saved_search_id: search.id,
    saved_search_name: search.name,
    search_criteria: criteria,
    applied_at: new Date().toISOString(),
    selected_property_ids: selectedPropertyIds,
    selected_floor_plan_keys: selectedFloorPlanKeys,
    selected_unit_ids: selectedUnitIds,
    units,
  };
}

function buildSavedSearchSummary(search: SavedSearch): string {
  const criteria = criteriaObject(search);
  const parts: string[] = [];
  const selectedPropertyIds = getExplicitSavedSearchPropertyIds(search);
  const selectedFloorPlanKeys = getSelectedFloorPlanKeys(search);
  const selectedUnitIds = getSelectedUnitIds(search);
  const neighborhoods = getCriteriaStrings(criteria, [
    "neighborhoods",
    "preferred_neighborhoods",
    "preferred_areas",
  ]);
  const maxBudget = getCriteriaNumber(criteria, ["max_price", "max_rent", "max_budget"]);

  if (selectedFloorPlanKeys.length > 0) {
    parts.push(
      `${selectedFloorPlanKeys.length} checked floor plan${
        selectedFloorPlanKeys.length === 1 ? "" : "s"
      }`
    );
  } else if (selectedPropertyIds.length > 0) {
    parts.push(
      `${selectedPropertyIds.length} checked propert${
        selectedPropertyIds.length === 1 ? "y" : "ies"
      }`
    );
  }
  if (selectedUnitIds.length > 0) {
    parts.push(`${selectedUnitIds.length} checked unit${selectedUnitIds.length === 1 ? "" : "s"}`);
  }
  if (neighborhoods.length > 0) {
    parts.push(neighborhoods.slice(0, 2).join(", "));
  }
  if (maxBudget !== null) {
    parts.push(`up to $${maxBudget.toLocaleString()}`);
  }

  return parts.join(" · ") || "No saved selections";
}

export default function ClientShortlistPanel({
  client,
  sentListings = EMPTY_SENT_LISTINGS,
  savedSearches = EMPTY_SAVED_SEARCHES,
}: ClientShortlistPanelProps) {
  const [detail, setDetail] = useState<ClientShortlistDetail | null>(null);
  const [properties, setProperties] = useState<ClientShortlistProperty[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<ClientShortlistProperty | null>(null);
  const [selectedSavedSearch, setSelectedSavedSearch] = useState<SavedSearch | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingNoteId, setSavingNoteId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingSmsConfirmation, setPendingSmsConfirmation] =
    useState<ShortlistSmsHandoffReceipt | null>(null);
  const [confirmingSms, setConfirmingSms] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [savedSearchSuggestionsOpen, setSavedSearchSuggestionsOpen] = useState(false);
  const [showAllSavedSearchSuggestions, setShowAllSavedSearchSuggestions] = useState(false);
  const [floorPlansByPropertyId, setFloorPlansByPropertyId] = useState<
    Record<string, FloorPlanLookupRow[]>
  >({});

  // Reuse the existing object when it is already empty. Writing a fresh `{}`
  // is never a no-op to React, so an unconditional clear re-renders — and the
  // effect that calls it would then run again on the new render.
  const clearFloorPlans = useCallback(() => {
    setFloorPlansByPropertyId((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, []);

  const propertyById = useMemo(
    () => new Map((detail?.properties || []).map((property) => [property.id, property])),
    [detail?.properties]
  );
  const listedPropertyIds = useMemo(
    () => new Set((detail?.items || []).map((item) => item.property_id)),
    [detail?.items]
  );
  const sentListSuggestions = useMemo(() => {
    const currentPropertyById = new Map(
      properties.map((property) => [property.id, property])
    );
    const seen = new Set<string>();

    return sentListings.flatMap((listing) => {
      const sentProperties = Array.isArray(listing.properties)
        ? listing.properties
        : [];

      return sentProperties.flatMap((sentProperty: any) => {
        const propertyId = sentProperty?.property_id || sentProperty?.id;
        if (
          !propertyId ||
          seen.has(propertyId) ||
          listedPropertyIds.has(propertyId)
        ) {
          return [];
        }

        const property = currentPropertyById.get(propertyId);
        if (!property) return [];

        seen.add(propertyId);
        const units = Array.isArray(sentProperty?.units)
          ? sentProperty.units
          : [];

        return [
          {
            property,
            sentAt: listing.sent_at,
            unitCount: units.length,
            metadata: buildSentListMetadata(listing, sentProperty),
          },
        ];
      });
    });
  }, [listedPropertyIds, properties, sentListings]);
  const savedSearchSuggestions = useMemo(() => {
    if (!selectedSavedSearch) return [];
    return properties
      .filter(
        (property) =>
          !listedPropertyIds.has(property.id) &&
          savedSearchMatchesProperty(selectedSavedSearch, property)
      )
      .map((property) => ({
        property,
        metadata: buildSavedSearchMetadata(selectedSavedSearch, property.id),
      }));
  }, [listedPropertyIds, properties, selectedSavedSearch]);

  useEffect(() => {
    const sentPropertyIds = sentListings.flatMap((listing) =>
      (Array.isArray(listing.properties) ? listing.properties : [])
        .map((sentProperty: any) => sentProperty?.property_id || sentProperty?.id)
        .filter((id: unknown): id is string => typeof id === "string" && id.length > 0)
    );
    const savedSearchPropertyIds = savedSearches.flatMap(getExplicitSavedSearchPropertyIds);
    const propertyIds = Array.from(new Set([...sentPropertyIds, ...savedSearchPropertyIds]));

    if (propertyIds.length === 0) {
      clearFloorPlans();
      return;
    }

    let cancelled = false;
    supabase
      .from("floor_plans")
      .select("id,property_id,model_name")
      .in("property_id", propertyIds)
      .then(({ data, error: floorPlanError }) => {
        if (cancelled) return;
        if (floorPlanError) {
          console.error("Error loading floor plans for sent-list suggestions:", floorPlanError);
          clearFloorPlans();
          return;
        }
        const grouped = (data || []).reduce<Record<string, FloorPlanLookupRow[]>>(
          (acc, row) => {
            if (!row.property_id) return acc;
            acc[row.property_id] = [...(acc[row.property_id] || []), row];
            return acc;
          },
          {}
        );
        setFloorPlansByPropertyId(grouped);
      });

    return () => {
      cancelled = true;
    };
  }, [clearFloorPlans, properties, savedSearches, sentListings]);
  const publicUrl = detail?.shortlist.public_token
    ? buildPublicShortlistUrl(detail.shortlist.public_token)
    : "";
  const linkDisabled = DEAD_LINK_STATUSES.has(client.status);

  useEffect(() => {
    loadProperties();
  }, []);

  useEffect(() => {
    loadExistingList();
  }, [client.id]);

  useEffect(() => {
    if (sentListSuggestions.length === 0) {
      setSuggestionsOpen(false);
      setShowAllSuggestions(false);
    }
  }, [sentListSuggestions.length]);

  useEffect(() => {
    if (
      selectedSavedSearch &&
      !savedSearches.some((search) => search.id === selectedSavedSearch.id)
    ) {
      setSelectedSavedSearch(null);
    }
  }, [savedSearches, selectedSavedSearch]);

  useEffect(() => {
    if (!selectedSavedSearch || savedSearchSuggestions.length === 0) {
      setSavedSearchSuggestionsOpen(false);
      setShowAllSavedSearchSuggestions(false);
      return;
    }
    setSavedSearchSuggestionsOpen(true);
  }, [savedSearchSuggestions.length, selectedSavedSearch]);

  const loadProperties = async () => {
    setLoadingProperties(true);
    try {
      const { data, error: propertyError } = await supabase
        .from("properties")
        .select("id,name,address,city,neighborhood,min_rent,max_rent,pets_allowed,available_floor_plans,total_floor_plans,s3_image_url,specials_summary,specials,updated_at")
        .order("name", { ascending: true })
        .limit(500);
      if (propertyError) throw propertyError;
      setProperties(data || []);
    } catch (err) {
      console.error("Error loading list properties:", err);
      setProperties([]);
    } finally {
      setLoadingProperties(false);
    }
  };

  const refreshDetail = async (shortlistId: string) => {
    const next = await getClientShortlistDetail(shortlistId);
    setDetail(next);
  };

  const loadExistingList = async () => {
    setLoading(true);
    setError(null);
    setDetail(null);
    try {
      const shortlists = await listClientShortlists(client.id);
      const shortlist = shortlists[0];
      if (!shortlist) return;
      setDetail({
        shortlist,
        items: [],
        properties: [],
        events: [],
      });
      try {
        await refreshDetail(shortlist.id);
      } catch (detailErr) {
        console.error("Error hydrating existing client list detail:", detailErr);
        setError(
          detailErr instanceof Error
            ? detailErr.message
            : "List found, but details did not finish loading"
        );
      }
    } catch (err) {
      console.error("Error loading existing client list:", err);
      setError(err instanceof Error ? err.message : "Unable to load client list");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenShortlist = async () => {
    setLoading(true);
    setError(null);
    try {
      const shortlist = await getOrCreateClientShortlist(client.id);
      setDetail((current) => ({
        shortlist,
        items: current?.shortlist.id === shortlist.id ? current.items : [],
        properties: current?.shortlist.id === shortlist.id ? current.properties : [],
        events: current?.shortlist.id === shortlist.id ? current.events : [],
      }));
      try {
        await refreshDetail(shortlist.id);
      } catch (detailErr) {
        console.error("Error hydrating client list detail:", detailErr);
        setError(
          detailErr instanceof Error
            ? detailErr.message
            : "List opened, but details did not finish loading"
        );
      }
    } catch (err) {
      console.error("Error opening client list:", err);
      setError(err instanceof Error ? err.message : "Unable to open list");
    } finally {
      setLoading(false);
    }
  };

  const handleAddProperty = async () => {
    if (!detail || !selectedProperty) return;
    setSaving(true);
    setError(null);
    try {
      await addClientShortlistItem(
        detail.shortlist.id,
        selectedProperty.id,
        (detail.items.length || 0) + 1
      );
      setSelectedProperty(null);
      await refreshDetail(detail.shortlist.id);
    } catch (err) {
      console.error("Error adding list property:", err);
      setError(err instanceof Error ? err.message : "Unable to add property");
    } finally {
      setSaving(false);
    }
  };

  const handleAddPropertyById = async (
    propertyId: string,
    metadataOverride?: Record<string, unknown>
  ) => {
    if (!detail) return;
    const suggestion =
      sentListSuggestions.find((item) => item.property.id === propertyId) ||
      savedSearchSuggestions.find((item) => item.property.id === propertyId);
    const metadata = enrichLegacySentListMetadata(
      metadataOverride || suggestion?.metadata,
      floorPlansByPropertyId[propertyId] || []
    );
    setSaving(true);
    setError(null);
    try {
      await addClientShortlistItem(
        detail.shortlist.id,
        propertyId,
        (detail.items.length || 0) + 1,
        metadata
      );
      await refreshDetail(detail.shortlist.id);
    } catch (err) {
      console.error("Error adding suggested list property:", err);
      setError(err instanceof Error ? err.message : "Unable to add property");
    } finally {
      setSaving(false);
    }
  };

  const handleAddAllSavedSearchSuggestions = async () => {
    if (!detail || savedSearchSuggestions.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await Promise.all(
        savedSearchSuggestions.map((suggestion, index) =>
          addClientShortlistItem(
            detail.shortlist.id,
            suggestion.property.id,
            (detail.items.length || 0) + index + 1,
            enrichLegacySentListMetadata(
              suggestion.metadata,
              floorPlansByPropertyId[suggestion.property.id] || []
            )
          )
        )
      );
      await refreshDetail(detail.shortlist.id);
    } catch (err) {
      console.error("Error adding saved-search list properties:", err);
      setError(err instanceof Error ? err.message : "Unable to add saved-search matches");
    } finally {
      setSaving(false);
    }
  };

  const handleAddAllSuggestions = async () => {
    if (!detail || sentListSuggestions.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await Promise.all(
        sentListSuggestions.map((suggestion, index) =>
          addClientShortlistItem(
            detail.shortlist.id,
            suggestion.property.id,
            (detail.items.length || 0) + index + 1,
            enrichLegacySentListMetadata(
              suggestion.metadata,
              floorPlansByPropertyId[suggestion.property.id] || []
            )
          )
        )
      );
      await refreshDetail(detail.shortlist.id);
    } catch (err) {
      console.error("Error adding suggested list properties:", err);
      setError(err instanceof Error ? err.message : "Unable to add suggested properties");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveProperty = async (itemId: string) => {
    if (!detail) return;
    setSaving(true);
    setError(null);
    try {
      await removeClientShortlistItem(detail.shortlist.id, itemId);
      await refreshDetail(detail.shortlist.id);
    } catch (err) {
      console.error("Error removing list property:", err);
      setError(err instanceof Error ? err.message : "Unable to remove property");
    } finally {
      setSaving(false);
    }
  };

  // Per-property "From your locator" note shown to the client on the link.
  // Saves on blur; no-ops when unchanged. On error we re-fetch so the field
  // reverts to server truth.
  const handleSaveNote = async (itemId: string, rawNote: string) => {
    if (!detail) return;
    const next = rawNote.trim();
    const item = detail.items.find((i) => i.id === itemId);
    const current = (item?.operator_note || "").trim();
    if (next === current) return;
    setSavingNoteId(itemId);
    setError(null);
    try {
      // Send "" (not null) to clear: the backend PATCH skips null fields, so a
      // null here would 400. Empty string writes through and renders nothing
      // on the client link (the /s/ render blanks a falsy note).
      await updateClientShortlistItem(detail.shortlist.id, itemId, {
        operator_note: next,
      });
      await refreshDetail(detail.shortlist.id);
    } catch (err) {
      console.error("Error saving list note:", err);
      setError(err instanceof Error ? err.message : "Unable to save note");
      await refreshDetail(detail.shortlist.id);
    } finally {
      setSavingNoteId(null);
    }
  };

  const handleCopyLink = async () => {
    if (!publicUrl || linkDisabled) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.error("Error copying list link:", err);
      setError("Copy failed. Open the link and copy it from the address bar.");
    }
  };

  const handleTextLink = async () => {
    if (!detail || !publicUrl || linkDisabled) return;
    try {
      const receipt = await handoffShortlistToNativeSms({
        client,
        shortlistId: detail.shortlist.id,
        publicUrl,
      });
      setPendingSmsConfirmation(receipt);
    } catch (err) {
      console.error("Error preparing shortlist text:", err);
      setError(err instanceof Error ? err.message : "Unable to prepare text");
    }
  };

  const handleConfirmSmsSent = async () => {
    if (!pendingSmsConfirmation) return;
    setConfirmingSms(true);
    try {
      await confirmShortlistSmsSent(pendingSmsConfirmation);
      setPendingSmsConfirmation(null);
    } catch (err) {
      console.error("Error marking shortlist text sent:", err);
      setError(err instanceof Error ? err.message : "Unable to mark text sent");
    } finally {
      setConfirmingSms(false);
    }
  };

  return (
    <Stack spacing={1.5}>
      <Paper
        variant="outlined"
        sx={{
          px: 1.25,
          py: 1,
          borderRadius: 1.5,
          backgroundColor: "rgba(255,255,255,0.015)",
        }}
      >
        <Stack spacing={0.65}>
          <Stack direction="row" justifyContent="space-between" gap={1} alignItems="center">
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 800, textTransform: "uppercase" }}
                >
                  Shortlist
                </Typography>
                {detail && (
                  <Chip
                    size="small"
                    label={linkDisabled ? "Dead link" : detail.shortlist.status}
                    color={linkDisabled ? "default" : "success"}
                    variant="outlined"
                    sx={{ height: 21, fontSize: "0.7rem" }}
                  />
                )}
              </Stack>
            </Box>
            {detail && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                <Tooltip title={copied ? "Copied" : "Copy link"}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleCopyLink}
                      disabled={linkDisabled}
                      sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.25 }}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={client.phone ? `Text it to ${client.first_name}` : "Client has no mobile number"}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`Text it to ${client.first_name}`}
                      onClick={handleTextLink}
                      disabled={linkDisabled || !client.phone}
                      sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.25 }}
                    >
                      <TextsmsIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Open link">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (!linkDisabled && publicUrl) {
                          window.open(publicUrl, "_blank", "noopener,noreferrer");
                        }
                      }}
                      disabled={linkDisabled}
                      sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.25 }}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            )}
          </Stack>

          {!detail ? (
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
              onClick={handleOpenShortlist}
              disabled={loading}
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
            >
              {loading ? "Loading Shortlist..." : "Create Shortlist"}
            </Button>
          ) : (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: linkDisabled ? "text.disabled" : "text.secondary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {publicUrl}
            </Typography>
          )}

          {linkDisabled && (
            <Alert severity="warning" sx={{ py: 0.25 }}>
              This client status disables the public link.
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}
          {pendingSmsConfirmation && (
            <Alert
              severity="info"
              action={
                <Stack direction="row" spacing={0.5}>
                  <Button
                    size="small"
                    onClick={() => setPendingSmsConfirmation(null)}
                    disabled={confirmingSms}
                  >
                    Didn't send
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleConfirmSmsSent}
                    disabled={confirmingSms}
                  >
                    {confirmingSms ? "Saving…" : "Yes, mark sent"}
                  </Button>
                </Stack>
              }
            >
              Did the text send?
            </Alert>
          )}
        </Stack>
      </Paper>

      {detail && (
        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5, backgroundColor: "rgba(255,255,255,0.015)" }}>
          <Stack spacing={1.1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 800, textTransform: "uppercase" }}
              >
                Properties ({detail.items.length})
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Autocomplete
                fullWidth
                loading={loadingProperties}
                options={properties.filter((property) => !listedPropertyIds.has(property.id))}
                value={selectedProperty}
                onChange={(_, value) => setSelectedProperty(value)}
                getOptionLabel={(option) =>
                  [option.name, option.neighborhood || option.city].filter(Boolean).join(" · ")
                }
                renderInput={(params) => (
                  <TextField {...params} size="small" placeholder="Add property" />
                )}
              />
              <Tooltip title="Add property to shortlist" arrow>
                <span>
                  <IconButton
                    aria-label="Add property to shortlist"
                    onClick={handleAddProperty}
                    disabled={!selectedProperty || saving}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.25,
                      border: "1px solid",
                      borderColor: selectedProperty
                        ? "primary.main"
                        : "rgba(255,255,255,0.14)",
                      color: selectedProperty ? "#fff" : "rgba(255,255,255,0.38)",
                      background: selectedProperty
                        ? "linear-gradient(135deg, #6D5DFB 0%, #A855F7 100%)"
                        : "rgba(255,255,255,0.03)",
                      flexShrink: 0,
                      "&:hover": {
                        background: selectedProperty
                          ? "linear-gradient(135deg, #5B4DE8 0%, #9333EA 100%)"
                          : "rgba(255,255,255,0.06)",
                      },
                      "&.Mui-disabled": {
                        borderColor: "rgba(255,255,255,0.12)",
                        color: "rgba(255,255,255,0.28)",
                        background: "rgba(255,255,255,0.025)",
                      },
                    }}
                  >
                    {saving ? <CircularProgress size={18} color="inherit" /> : <AddIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>

            {savedSearches.length > 0 && (
              <Box
                sx={{
                  pt: 0.75,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Autocomplete
                  fullWidth
                  size="small"
                  options={savedSearches}
                  value={selectedSavedSearch}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_, value) => {
                    setSelectedSavedSearch(value);
                    setShowAllSavedSearchSuggestions(false);
                    setSavedSearchSuggestionsOpen(Boolean(value));
                  }}
                  getOptionLabel={(option) => option.name || "Saved search"}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 650 }}>
                          {option.name || "Saved search"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {buildSavedSearchSummary(option)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Add saved selections" />
                  )}
                />
                {selectedSavedSearch && (
                  <Box sx={{ mt: 0.75 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                      sx={{ py: 0.35 }}
                    >
                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        onClick={() => {
                          if (savedSearchSuggestions.length === 0) return;
                          setSavedSearchSuggestionsOpen((open) => {
                            if (open) setShowAllSavedSearchSuggestions(false);
                            return !open;
                          });
                        }}
                        sx={{
                          minWidth: 0,
                          cursor: savedSearchSuggestions.length > 0 ? "pointer" : "default",
                          userSelect: "none",
                        }}
                      >
                        <ExpandMoreIcon
                          sx={{
                            fontSize: 18,
                            color: "text.secondary",
                            transform: savedSearchSuggestionsOpen
                              ? "rotate(0deg)"
                              : "rotate(-90deg)",
                            transition: "transform 140ms ease",
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 800,
                            textTransform: "uppercase",
                          }}
                        >
                          Saved search selections
                        </Typography>
                        <Chip
                          size="small"
                          label={`${savedSearchSuggestions.length} selected ${
                            savedSearchSuggestions.length === 1 ? "property" : "properties"
                          }`}
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.68rem" }}
                        />
                      </Stack>
                      {savedSearchSuggestions.length > 1 && (
                        <Button
                          size="small"
                          variant="text"
                          onClick={handleAddAllSavedSearchSuggestions}
                          disabled={saving}
                          sx={{
                            minWidth: 0,
                            px: 0.5,
                            textTransform: "none",
                            fontWeight: 700,
                          }}
                        >
                          Add all
                        </Button>
                      )}
                    </Stack>
                    {savedSearchSuggestions.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        {savedSearchHasSelectedUnits(selectedSavedSearch) &&
                        !savedSearchHasSelectedFloorPlans(selectedSavedSearch)
                          ? "This search saved exact units. Unit import is not available in this panel yet."
                          : "No unchecked saved selections are available for this search."}
                      </Typography>
                    ) : (
                      <Collapse in={savedSearchSuggestionsOpen} timeout={140} unmountOnExit>
                        <Stack
                          divider={<Box sx={{ borderTop: "1px solid", borderColor: "divider" }} />}
                          spacing={0}
                          sx={{ mt: 0.35 }}
                        >
                          {savedSearchSuggestions
                            .slice(
                              0,
                              showAllSavedSearchSuggestions
                                ? savedSearchSuggestions.length
                                : 3
                            )
                            .map((suggestion) => {
                              const resolvedMetadata = enrichLegacySentListMetadata(
                                suggestion.metadata,
                                floorPlansByPropertyId[suggestion.property.id] || []
                              );
                              const floorPlanCount = getFloorPlanCount(resolvedMetadata);

                              return (
                                <Stack
                                  key={suggestion.property.id}
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                  sx={{ py: 0.75, px: 0.25 }}
                                >
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 650,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {suggestion.property.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {[
                                        suggestion.property.neighborhood || suggestion.property.city,
                                        formatRent(suggestion.property),
                                        floorPlanCount
                                          ? `${floorPlanCount} floor plan${
                                              floorPlanCount === 1 ? "" : "s"
                                            }`
                                          : null,
                                      ]
                                        .filter(Boolean)
                                        .join(" · ")}
                                    </Typography>
                                  </Box>
                                  <Tooltip title="Add to list" arrow>
                                    <span>
                                      <IconButton
                                        aria-label={`Add ${suggestion.property.name} to list`}
                                        size="small"
                                        onClick={() =>
                                          handleAddPropertyById(
                                            suggestion.property.id,
                                            suggestion.metadata
                                          )
                                        }
                                        disabled={saving}
                                        sx={{
                                          width: 34,
                                          height: 34,
                                          borderRadius: 1,
                                          border: "1px solid",
                                          borderColor: "rgba(124, 111, 255, 0.55)",
                                          color: "#8B7CFF",
                                          backgroundColor: "rgba(124, 111, 255, 0.08)",
                                          flexShrink: 0,
                                          "&:hover": {
                                            backgroundColor: "rgba(124, 111, 255, 0.16)",
                                            borderColor: "rgba(124, 111, 255, 0.8)",
                                          },
                                          "&.Mui-disabled": {
                                            borderColor: "rgba(255,255,255,0.12)",
                                            color: "rgba(255,255,255,0.28)",
                                            backgroundColor: "rgba(255,255,255,0.025)",
                                          },
                                        }}
                                      >
                                        <AddIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Stack>
                              );
                            })}
                          {savedSearchSuggestions.length > 3 &&
                            !showAllSavedSearchSuggestions && (
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => setShowAllSavedSearchSuggestions(true)}
                                sx={{
                                  alignSelf: "flex-start",
                                  minWidth: 0,
                                  px: 0.25,
                                  py: 0.5,
                                  textTransform: "none",
                                  color: "text.secondary",
                                  fontWeight: 600,
                                  "&:hover": { color: "primary.main" },
                                }}
                              >
                                + {savedSearchSuggestions.length - 3} more saved selections
                              </Button>
                            )}
                        </Stack>
                      </Collapse>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {sentListSuggestions.length > 0 && (
              <Box
                sx={{
                  pt: 0.75,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  sx={{ py: 0.35 }}
                >
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    onClick={() => {
                      setSuggestionsOpen((open) => {
                        if (open) setShowAllSuggestions(false);
                        return !open;
                      });
                    }}
                    sx={{
                      minWidth: 0,
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    <ExpandMoreIcon
                      sx={{
                        fontSize: 18,
                        color: "text.secondary",
                        transform: suggestionsOpen ? "rotate(0deg)" : "rotate(-90deg)",
                        transition: "transform 140ms ease",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 800,
                        textTransform: "uppercase",
                      }}
                    >
                      Suggested from sent lists
                    </Typography>
                    <Chip
                      size="small"
                      label={`${sentListSuggestions.length} ${
                        sentListSuggestions.length === 1 ? "property" : "properties"
                      }`}
                      variant="outlined"
                      sx={{ height: 20, fontSize: "0.68rem" }}
                    />
                  </Stack>
                  {sentListSuggestions.length > 1 && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={handleAddAllSuggestions}
                      disabled={saving}
                      sx={{
                        minWidth: 0,
                        px: 0.5,
                        textTransform: "none",
                        fontWeight: 700,
                      }}
                    >
                      Add all
                    </Button>
                  )}
                </Stack>
                <Collapse in={suggestionsOpen} timeout={140} unmountOnExit>
                  <Stack
                    divider={<Box sx={{ borderTop: "1px solid", borderColor: "divider" }} />}
                    spacing={0}
                    sx={{ mt: 0.35 }}
                  >
                    {sentListSuggestions
                      .slice(0, showAllSuggestions ? sentListSuggestions.length : 3)
                      .map((suggestion) => (
                        <Stack
                          key={suggestion.property.id}
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{
                            py: 0.75,
                            px: 0.25,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 650,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {suggestion.property.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {[
                                `Sent ${formatRelativeTime(suggestion.sentAt)}`,
                                suggestion.unitCount
                                  ? `${suggestion.unitCount} ${suggestion.unitCount === 1 ? "unit" : "units"}`
                                  : null,
                                suggestion.property.neighborhood || suggestion.property.city,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </Typography>
                          </Box>
                          <Tooltip title="Add to list" arrow>
                            <span>
                              <IconButton
                                aria-label={`Add ${suggestion.property.name} to list`}
                                size="small"
                                onClick={() => handleAddPropertyById(suggestion.property.id)}
                                disabled={saving}
                                sx={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: 1,
                                  border: "1px solid",
                                  borderColor: "rgba(124, 111, 255, 0.55)",
                                  color: "#8B7CFF",
                                  backgroundColor: "rgba(124, 111, 255, 0.08)",
                                  flexShrink: 0,
                                  "&:hover": {
                                    backgroundColor: "rgba(124, 111, 255, 0.16)",
                                    borderColor: "rgba(124, 111, 255, 0.8)",
                                  },
                                  "&.Mui-disabled": {
                                    borderColor: "rgba(255,255,255,0.12)",
                                    color: "rgba(255,255,255,0.28)",
                                    backgroundColor: "rgba(255,255,255,0.025)",
                                  },
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                    ))}
                    {sentListSuggestions.length > 3 && !showAllSuggestions && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setShowAllSuggestions(true)}
                        sx={{
                          alignSelf: "flex-start",
                          minWidth: 0,
                          px: 0.25,
                          py: 0.5,
                          textTransform: "none",
                          color: "text.secondary",
                          fontWeight: 600,
                          "&:hover": { color: "primary.main" },
                        }}
                      >
                        + {sentListSuggestions.length - 3} more available from sent lists
                      </Button>
                    )}
                  </Stack>
                </Collapse>
              </Box>
            )}

            {detail.items.length === 0 ? (
              <Alert severity="info">No properties added yet.</Alert>
            ) : (
              <Stack divider={<Box sx={{ borderTop: "1px solid", borderColor: "divider" }} />} spacing={0}>
                {detail.items.map((item) => {
                  const property = propertyById.get(item.property_id);
                  return (
                    <Stack key={item.id} spacing={0.75} sx={{ py: 1.25 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 650, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {property?.name || item.property_id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {property
                              ? [
                                  property.neighborhood || property.city,
                                  formatRent(property),
                                  getFloorPlanCount(item.metadata)
                                    ? `${getFloorPlanCount(item.metadata)} ${
                                        getFloorPlanCount(item.metadata) === 1
                                          ? "floor plan"
                                          : "floor plans"
                                      }`
                                    : null,
                                ].filter(Boolean).join(" · ")
                              : "Property details unavailable"}
                          </Typography>
                        </Box>
                        <Tooltip title="Remove from list">
                          <IconButton
                            onClick={() => handleRemoveProperty(item.id)}
                            disabled={saving}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      {/* PKT-0240: operator writes the client-facing
                          "From your locator" note here. Saves on blur. */}
                      <TextField
                        key={`note-${item.id}-${item.operator_note ?? ""}`}
                        fullWidth
                        size="small"
                        multiline
                        maxRows={3}
                        defaultValue={item.operator_note ?? ""}
                        placeholder={
                          'Note the client sees ("From your locator") — e.g. "Closest to your office, 6-min drive"'
                        }
                        onBlur={(e) => handleSaveNote(item.id, e.target.value)}
                        disabled={savingNoteId === item.id}
                        sx={{ "& .MuiInputBase-input": { fontSize: 13, lineHeight: 1.4 } }}
                      />
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </Paper>
      )}

      {detail && (
        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5, backgroundColor: "rgba(255,255,255,0.015)" }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontWeight: 800, textTransform: "uppercase", mb: 1, display: "block" }}
          >
            Recent Activity
          </Typography>
          {detail.events.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No client interactions yet.
            </Typography>
          ) : (
            <Stack spacing={0.75}>
              {detail.events.slice(0, 8).map((event) => (
                <Box key={event.id} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <Typography variant="body2">
                    {event.event_type.replace(/_/g, " ")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(event.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Paper>
      )}
    </Stack>
  );
}
